import React, { FormEvent, useMemo, useState } from "react";
import {
  ClientValue,
  HomeworkCompletionStatus,
  HomeworkUpdateFields,
  PortalActivityImage,
  PortalHomeworkDetail,
  PortalPatient,
  Weekday,
  homeworkCompletionStatuses,
  weekdays,
} from "../api/contracts";
import { parseActivitySteps } from "../activitySteps/activityStepsParser";
import { activityStepsAreReady } from "../activitySteps/activityStepsValidation";
import {
  PillPaletteToken,
  getHomeworkCompletionStatusPillToken,
  getHomeworkRepeatPillToken,
  getWeekdayPillToken,
} from "../config/palette";
import { homeworkStepsEditorPath } from "../config/activityStepsNavigation";
import { formatPortalDate, formatPortalDateTime } from "../utils/dateTime";
import {
  ActivityImageGallery,
  ActivityImageUploadField,
} from "./ActivityImages";
import {
  ActivityTypePill,
  BooleanIndicator,
  ContentSection,
  DisplayValue,
  RecordDetailCard,
  RecordFieldRow,
} from "./DataDisplay";
import { MultiSelectPillField } from "./MultiSelectPillField";
import { HomeworkActivityFieldHelp } from "./HomeworkActivityFieldHelp";
import { PortalLink } from "./PortalLink";
import { SingleSelectPillField } from "./SingleSelectPillField";

type RepeatChoice = "" | "Daily" | "Weekly";

export interface HomeworkFormValues {
  valueSupportedId: string;
  nonExposureHomework: boolean;
  activitySteps: string;
  alternative: string;
  coachingPrompts: string;
  points: string;
  coins: string;
  dueDate: string;
  repeats: RepeatChoice;
  repeatOn: Weekday[];
  recurrenceEndDate: string;
  completionStatus: HomeworkCompletionStatus;
  summary: string;
  firstSuds: string;
  secondSuds: string;
  sentimentAnalysis: string;
  copingSkills: string;
  altered: string;
  routineTime: string;
}

type HomeworkFormErrors = Partial<
  Record<
    | "points"
    | "coins"
    | "activitySteps"
    | "repeatOn"
    | "recurrenceEndDate"
    | "firstSuds"
    | "secondSuds"
    | "form",
    string
  >
>;

export function homeworkFormValues(
  homework: PortalHomeworkDetail,
): HomeworkFormValues {
  return {
    valueSupportedId: homework.valueSupportedId,
    nonExposureHomework: homework.nonExposureHomework,
    activitySteps: homework.activitySteps,
    alternative: homework.alternative || homework.alternativeSteps,
    coachingPrompts: homework.coachingPrompts,
    points: String(homework.points),
    coins: String(homework.coins),
    dueDate: homework.dueDate,
    repeats: homework.isRecurring ? homework.repeats : "",
    repeatOn:
      homework.isRecurring && homework.repeats === "Weekly"
        ? [...homework.repeatOn]
        : [],
    recurrenceEndDate: homework.isRecurring
      ? (homework.recurrenceEndDate ?? "")
      : "",
    completionStatus: homework.completionStatus,
    summary: homework.summary,
    firstSuds: homework.firstSuds === "" ? "" : String(homework.firstSuds),
    secondSuds: homework.secondSuds === "" ? "" : String(homework.secondSuds),
    sentimentAnalysis: homework.sentimentAnalysis,
    copingSkills: homework.copingSkills,
    altered: homework.altered,
    routineTime: homework.routineTime,
  };
}

function numericSuds(value: string): number | null {
  return value.trim() === "" ? null : Number(value);
}

export function validateHomeworkForm(
  values: HomeworkFormValues,
  initialValues?: HomeworkFormValues,
): HomeworkFormErrors {
  const errors: HomeworkFormErrors = {};
  if (values.points.trim() === "" || !Number.isInteger(Number(values.points)))
    errors.points = "Activity Points must be an integer.";
  if (values.coins.trim() === "" || !Number.isInteger(Number(values.coins)))
    errors.coins = "Activity Coins must be an integer.";
  for (const [key, label] of [
    ["firstSuds", "Pre-Exposure SUDS"],
    ["secondSuds", "Post-Exposure SUDS"],
  ] as const) {
    const raw = values[key].trim();
    const number = Number(raw);
    if (raw !== "" && (!Number.isInteger(number) || number < 0 || number > 10))
      errors[key] = `${label} must be blank or an integer from 0 through 10.`;
  }
  if (values.dueDate && values.repeats === "Weekly" && !values.repeatOn.length)
    errors.repeatOn = "Select at least one weekday for a weekly recurrence.";
  if (values.dueDate && values.repeats) {
    if (!values.recurrenceEndDate)
      errors.recurrenceEndDate = "Recurrence End Date is required.";
    else if (values.recurrenceEndDate < values.dueDate)
      errors.recurrenceEndDate =
        "Recurrence End Date must be on or after Due Date.";
  }
  if (
    initialValues &&
    values.activitySteps !== initialValues.activitySteps &&
    !activityStepsAreReady(parseActivitySteps(values.activitySteps))
  ) {
    errors.activitySteps =
      "Activity Steps must contain at least one complete step.";
  }
  return errors;
}

export function buildSparseHomeworkUpdate(
  initial: HomeworkFormValues,
  current: HomeworkFormValues,
): HomeworkUpdateFields {
  const payload: HomeworkUpdateFields = {};
  const assign = <K extends keyof HomeworkUpdateFields>(
    key: K,
    value: HomeworkUpdateFields[K],
    before: unknown,
  ) => {
    if (JSON.stringify(value) !== JSON.stringify(before)) payload[key] = value;
  };
  assign(
    "valueSupportedId",
    current.valueSupportedId,
    initial.valueSupportedId,
  );
  assign(
    "nonExposureHomework",
    current.nonExposureHomework,
    initial.nonExposureHomework,
  );
  assign("activitySteps", current.activitySteps, initial.activitySteps);
  assign("alternative", current.alternative, initial.alternative);
  assign("coachingPrompts", current.coachingPrompts, initial.coachingPrompts);
  assign("points", Number(current.points), Number(initial.points));
  assign("coins", Number(current.coins), Number(initial.coins));
  assign("dueDate", current.dueDate, initial.dueDate);
  assign(
    "completionStatus",
    current.completionStatus,
    initial.completionStatus,
  );
  assign("summary", current.summary, initial.summary);
  assign(
    "firstSuds",
    numericSuds(current.firstSuds),
    numericSuds(initial.firstSuds),
  );
  assign(
    "secondSuds",
    numericSuds(current.secondSuds),
    numericSuds(initial.secondSuds),
  );
  assign(
    "sentimentAnalysis",
    current.sentimentAnalysis,
    initial.sentimentAnalysis,
  );
  assign("copingSkills", current.copingSkills, initial.copingSkills);
  assign("altered", current.altered, initial.altered);
  assign("routineTime", current.routineTime, initial.routineTime);

  const normalized = current.dueDate
    ? {
        isRecurring: current.repeats !== "",
        repeats: current.repeats,
        repeatOn: current.repeats === "Weekly" ? current.repeatOn : [],
        recurrenceEndDate: current.repeats ? current.recurrenceEndDate : "",
      }
    : {
        isRecurring: false,
        repeats: "" as const,
        repeatOn: [] as Weekday[],
        recurrenceEndDate: "",
      };
  const original = initial.dueDate
    ? {
        isRecurring: initial.repeats !== "",
        repeats: initial.repeats,
        repeatOn: initial.repeats === "Weekly" ? initial.repeatOn : [],
        recurrenceEndDate: initial.repeats ? initial.recurrenceEndDate : "",
      }
    : {
        isRecurring: false,
        repeats: "" as const,
        repeatOn: [] as Weekday[],
        recurrenceEndDate: "",
      };

  if (JSON.stringify(normalized) !== JSON.stringify(original)) {
    payload.isRecurring = normalized.isRecurring;
    payload.repeats = normalized.repeats;
    payload.repeatOn = normalized.repeatOn;
    payload.recurrenceEndDate = normalized.recurrenceEndDate;
  }
  return payload;
}

const neutral = (): PillPaletteToken => "grayLight2";

export function HomeworkForm({
  homework,
  values: clientValues,
  clinicianCode,
  patientId,
  client,
  isSubmitting,
  submissionError,
  onUploadImage,
  onRemoveImage,
  onSubmit,
  onCancel,
}: {
  homework: PortalHomeworkDetail;
  values: readonly ClientValue[];
  clinicianCode: string;
  patientId: string;
  client?: Pick<PortalPatient, "id" | "accessCode" | "nickname">;
  isSubmitting: boolean;
  submissionError?: unknown;
  onUploadImage: (file: File) => Promise<PortalActivityImage>;
  onRemoveImage: (imageId: string) => Promise<void>;
  onSubmit: (fields: HomeworkUpdateFields) => Promise<void>;
  onCancel: () => void;
}) {
  const initial = useMemo(() => homeworkFormValues(homework), [homework]);
  const [form, setForm] = useState(initial);
  const [activityImages, setActivityImages] = useState<PortalActivityImage[]>(
    () => [...(homework.activityImages ?? [])],
  );
  const [errors, setErrors] = useState<HomeworkFormErrors>({});
  const selectedValue = clientValues.find(
    (value) => value.id === form.valueSupportedId,
  );
  const valueOptions = [
    ...(selectedValue && !selectedValue.enabled ? [selectedValue] : []),
    ...clientValues.filter((value) => value.enabled),
  ]
    .filter(
      (value, index, all) =>
        all.findIndex((candidate) => candidate.id === value.id) === index,
    )
    .map((value) => ({
      id: value.id,
      value: value.id,
      label: value.valueName,
      disabled: !value.enabled,
    }));
  if (form.valueSupportedId && !selectedValue) {
    valueOptions.unshift({
      id: form.valueSupportedId,
      value: form.valueSupportedId,
      label: "Current Value (Unavailable)",
      disabled: true,
    });
  }
  const set = <K extends keyof HomeworkFormValues>(
    key: K,
    value: HomeworkFormValues[K],
  ) => {
    setErrors((current) => ({ ...current, [key]: undefined, form: undefined }));
    setForm((current) => ({ ...current, [key]: value }));
  };
  const handleDueDate = (dueDate: string) => {
    setForm((current) => {
      if (!dueDate)
        return {
          ...current,
          dueDate: "",
          repeats: "",
          repeatOn: [],
          recurrenceEndDate: "",
        };
      const followsDueDate = Boolean(
        current.dueDate && current.recurrenceEndDate === current.dueDate,
      );
      return {
        ...current,
        dueDate,
        recurrenceEndDate:
          current.repeats && (!current.recurrenceEndDate || followsDueDate)
            ? dueDate
            : current.recurrenceEndDate,
      };
    });
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (isSubmitting) return;
    const nextErrors = validateHomeworkForm(form, initial);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    const fields = buildSparseHomeworkUpdate(initial, form);
    if (!Object.keys(fields).length) return onCancel();
    await onSubmit(fields);
  };
  const input = (
    key: keyof HomeworkFormValues,
    label: string,
    type = "text",
  ) => (
    <div
      className={`cp-form-field cp-homework-edit-control${
        type === "date" ? " cp-homework-edit-date-control" : ""
      }`}
    >
      <label className="cp-visually-hidden" htmlFor={`homework-${key}`}>
        {label}
      </label>
      <input
        id={`homework-${key}`}
        className={`cp-form-input${
          key === "points" || key === "coins"
            ? " cp-form-input--no-spinner"
            : ""
        }`}
        type={type}
        value={String(form[key])}
        aria-invalid={Boolean(errors[key as keyof HomeworkFormErrors])}
        onChange={(event) =>
          key === "dueDate"
            ? handleDueDate(event.target.value)
            : set(key as never, event.target.value as never)
        }
      />
      {errors[key as keyof HomeworkFormErrors] ? (
        <p className="cp-form-error">
          {errors[key as keyof HomeworkFormErrors]}
        </p>
      ) : null}
    </div>
  );
  const textarea = (key: keyof HomeworkFormValues, label: string) => (
    <div className="cp-form-field cp-homework-edit-control">
      <label className="cp-visually-hidden" htmlFor={`homework-${key}`}>
        {label}
      </label>
      <textarea
        id={`homework-${key}`}
        className="cp-form-input cp-form-textarea"
        rows={3}
        value={String(form[key])}
        onChange={(event) => set(key as never, event.target.value as never)}
      />
    </div>
  );
  const dirty = Object.keys(buildSparseHomeworkUpdate(initial, form)).length > 0;
  const confirmStepEditorNavigation = (
    event: React.MouseEvent<HTMLAnchorElement>,
  ) => {
    if (
      dirty &&
      !window.confirm(
        "Opening the Step Editor will discard unsaved changes on this form. Continue?",
      )
    ) {
      event.preventDefault();
    }
  };

  return (
    <form
      className="cp-homework-edit-form"
      aria-label="Edit Homework"
      onSubmit={submit}
      noValidate
    >
      <ContentSection title="Homework Activity Details">
        <RecordDetailCard>
          <RecordFieldRow label="Homework Activity">
            <DisplayValue value={homework.homeworkName} />
          </RecordFieldRow>
          {client ? (
            <RecordFieldRow label="Client">
              <PortalLink
                path={`/clinician-portal/clients/${encodeURIComponent(client.id)}/profile`}
                clinicianCode={clinicianCode}
              >
                {client.accessCode} • {client.nickname}
              </PortalLink>
            </RecordFieldRow>
          ) : null}
          <RecordFieldRow label="Activity Type">
            <ActivityTypePill activityType={homework.activityType} />
          </RecordFieldRow>
          <RecordFieldRow label="Value Supported">
            <div className="cp-form-field cp-homework-edit-control">
              <SingleSelectPillField
                id="homework-value"
                value={form.valueSupportedId}
                options={valueOptions}
                onChange={(value) => set("valueSupportedId", value)}
                ariaLabel="Value Supported"
                placeholder="Select a client value"
                searchPlaceholder="Find a value"
                emptyMessage="No client values found"
                resolveColor={neutral}
                clearable
              />
            </div>
          </RecordFieldRow>
          <RecordFieldRow label="Non-Exposure Homework">
            <HomeworkActivityFieldHelp field="nonExposureHomework" />
            <label className="cp-form-checkbox cp-homework-edit-checkbox">
              <input
                type="checkbox"
                aria-label="Non-Exposure Homework"
                checked={form.nonExposureHomework}
                onChange={(event) =>
                  set("nonExposureHomework", event.target.checked)
                }
              />
              {form.nonExposureHomework ? "Yes" : "No"}
            </label>
          </RecordFieldRow>
          <RecordFieldRow label="Assigned Date">
            {formatPortalDate(homework.assignedDate)}
          </RecordFieldRow>
          <RecordFieldRow label="Due Date">
            <HomeworkActivityFieldHelp field="dueDate" />
            {input("dueDate", "Due Date", "date")}
          </RecordFieldRow>
          {form.dueDate ? (
            <RecordFieldRow label="Repeats">
              <HomeworkActivityFieldHelp field="repeats" />
              <div className="cp-form-field cp-homework-edit-control">
                <SingleSelectPillField
                  id="homework-repeats"
                  value={form.repeats}
                  options={[
                    {
                      id: "does-not-repeat",
                      value: "",
                      label: "Does not repeat",
                    },
                    { id: "daily", value: "Daily", label: "Daily" },
                    { id: "weekly", value: "Weekly", label: "Weekly" },
                  ]}
                  onChange={(value) => {
                    const repeats = value as RepeatChoice;
                    setForm((current) => ({
                      ...current,
                      repeats,
                      repeatOn: repeats === "Weekly" ? current.repeatOn : [],
                      recurrenceEndDate: repeats
                        ? current.recurrenceEndDate || current.dueDate
                        : "",
                    }));
                  }}
                  ariaLabel="Repeats"
                  placeholder="Select a recurrence"
                  searchPlaceholder="Find a recurrence"
                  emptyMessage="No recurrence options found"
                  resolveColor={(option) =>
                    getHomeworkRepeatPillToken(option.value)
                  }
                />
              </div>
            </RecordFieldRow>
          ) : null}
          {form.dueDate && form.repeats === "Weekly" ? (
            <RecordFieldRow label="Repeat On">
              <div className="cp-form-field cp-homework-edit-control">
                <MultiSelectPillField
                  value={form.repeatOn}
                  options={weekdays.map((day) => ({
                    id: day,
                    value: day,
                    label: day,
                  }))}
                  onChange={(value) => set("repeatOn", value as Weekday[])}
                  ariaLabel="Repeat On weekdays"
                  placeholder="Select weekdays"
                  searchPlaceholder="Find a weekday"
                  emptyMessage="No weekdays found"
                  resolveColor={(option) => getWeekdayPillToken(option.value)}
                  invalid={Boolean(errors.repeatOn)}
                />
                {errors.repeatOn ? (
                  <p className="cp-form-error">{errors.repeatOn}</p>
                ) : null}
              </div>
            </RecordFieldRow>
          ) : null}
          {form.dueDate && form.repeats ? (
            <RecordFieldRow label="Recurrence End Date">
              <HomeworkActivityFieldHelp field="recurrenceEndDate" />
              {input("recurrenceEndDate", "Recurrence End Date", "date")}
            </RecordFieldRow>
          ) : null}
          <RecordFieldRow label="Activity Points">
            {input("points", "Activity Points", "number")}
          </RecordFieldRow>
          <RecordFieldRow label="Activity Coins">
            {input("coins", "Activity Coins", "number")}
          </RecordFieldRow>
          <RecordFieldRow label="Activity Steps">
            <HomeworkActivityFieldHelp field="activitySteps" />
            <div className="cp-detail-value-with-action cp-homework-steps-control">
              <div className="cp-form-field cp-homework-edit-control">
                <label
                  className="cp-visually-hidden"
                  htmlFor="homework-activitySteps"
                >
                  Activity Steps
                </label>
                <textarea
                  id="homework-activitySteps"
                  className="cp-form-input cp-form-textarea"
                  rows={4}
                  value={form.activitySteps}
                  aria-invalid={Boolean(errors.activitySteps)}
                  aria-describedby="homework-activitySteps-help"
                  onChange={(event) =>
                    set("activitySteps", event.target.value)
                  }
                />
                <p
                  className="cp-form-help"
                  id="homework-activitySteps-help"
                >
                  Edit text directly, or use the Step Editor for structured
                  steps such as timers, images, videos, and uploads.
                </p>
                {errors.activitySteps ? (
                  <p className="cp-form-error">{errors.activitySteps}</p>
                ) : null}
              </div>
              <a
                className="cp-edit-steps-button"
                href={homeworkStepsEditorPath({
                  clinicianCode,
                  patientId,
                  homeworkAssignmentId: homework.homeworkAssignmentId,
                  returnUrl: window.location.href,
                })}
                onClick={confirmStepEditorNavigation}
              >
                Open Step Editor
              </a>
            </div>
          </RecordFieldRow>
          <RecordFieldRow label="Modification Steps">
            <HomeworkActivityFieldHelp field="modificationSteps" />
            {textarea("alternative", "Modification Steps")}
          </RecordFieldRow>
          <RecordFieldRow label="Coaching Prompts">
            <HomeworkActivityFieldHelp field="coachingPrompts" />
            {textarea("coachingPrompts", "Coaching Prompts")}
          </RecordFieldRow>
          <RecordFieldRow label="Activity Images">
            <HomeworkActivityFieldHelp field="activityImages" />
            <ActivityImageUploadField
              images={activityImages}
              disabled={isSubmitting}
              onUpload={async (file) => {
                const image = await onUploadImage(file);
                setActivityImages((current) => [
                  ...current.filter((candidate) => candidate.id !== image.id),
                  image,
                ]);
              }}
              onRemove={async (imageId) => {
                await onRemoveImage(imageId);
                setActivityImages((current) =>
                  current.filter((image) => image.id !== imageId),
                );
              }}
            />
          </RecordFieldRow>
        </RecordDetailCard>
      </ContentSection>

      <ContentSection title="Homework Outcome">
        <RecordDetailCard>
          <RecordFieldRow label="Overdue">
            <BooleanIndicator value={homework.overdue} />
          </RecordFieldRow>
          <RecordFieldRow label="Email Alert">
            <BooleanIndicator value={homework.emailAlert} />
          </RecordFieldRow>
          <RecordFieldRow label="Completion Status">
            <div className="cp-form-field cp-homework-edit-control">
              <SingleSelectPillField
                id="homework-completionStatus"
                value={form.completionStatus}
                options={homeworkCompletionStatuses.map((status) => ({
                  id: status.toLocaleLowerCase().replace(/\s+/g, "-"),
                  value: status,
                  label: status,
                }))}
                onChange={(value) =>
                  set("completionStatus", value as HomeworkCompletionStatus)
                }
                ariaLabel="Completion Status"
                placeholder="Select a Completion Status"
                searchPlaceholder="Find a Completion Status"
                emptyMessage="No Completion Statuses found"
                resolveColor={(option) =>
                  getHomeworkCompletionStatusPillToken(option.value)
                }
              />
            </div>
          </RecordFieldRow>
          <RecordFieldRow label="Completed Date">
            {formatPortalDate(homework.completedDate)}
          </RecordFieldRow>
          <RecordFieldRow label="Summary">
            {textarea("summary", "Summary")}
          </RecordFieldRow>
          <RecordFieldRow label="Pre-Exposure SUDS">
            {input("firstSuds", "Pre-Exposure SUDS", "number")}
          </RecordFieldRow>
          <RecordFieldRow label="Post-Exposure SUDS">
            {input("secondSuds", "Post-Exposure SUDS", "number")}
          </RecordFieldRow>
          <RecordFieldRow label="Sentiment Analysis">
            {textarea("sentimentAnalysis", "Sentiment Analysis")}
          </RecordFieldRow>
          <RecordFieldRow label="Coping Skills Used">
            {textarea("copingSkills", "Coping Skills Used")}
          </RecordFieldRow>
          <RecordFieldRow label="Steps Altered">
            {textarea("altered", "Steps Altered")}
          </RecordFieldRow>
          <RecordFieldRow label="Routine Time">
            {input("routineTime", "Routine Time")}
          </RecordFieldRow>
          <RecordFieldRow label="Client Uploads">
            <ActivityImageGallery images={homework.clientUploads ?? []} />
          </RecordFieldRow>
          <RecordFieldRow label="Conversation">
            {homework.conversationReportUrl ? (
              <a
                className="cp-conversation-link"
                href={homework.conversationReportUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                View Log
              </a>
            ) : (
              <span className="cp-empty-value">Not available</span>
            )}
          </RecordFieldRow>
          <RecordFieldRow label="Created">
            {formatPortalDateTime(homework.createdAt)}
          </RecordFieldRow>
        </RecordDetailCard>
      </ContentSection>

      {submissionError ? (
        <p className="cp-form-error cp-form-error--panel" role="alert">
          The Homework could not be saved. Please review the form and try
          again.
        </p>
      ) : null}
      <div className="cp-form-actions cp-form-actions--sticky">
        <button
          type="button"
          className="cp-button cp-button--secondary"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="cp-button cp-button--primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}
